import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useGetFormQuery,
  useSubmitFormResponseMutation,
  useGetUploadUrlMutation,
  useGetMyInvitationsQuery,
} from '../store/api/localApi';
import { useLocalGetStudentsQuery, useLocalGetStudentsByParentQuery } from '../store/api/localApi';
import { responseKey } from './formFieldKey';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import { useScopedPath } from '../components/ScopedLink';
import { FormFieldType, FormType, isFieldTypeAllowedForFormType } from '../types';
import FieldRenderer from '../components/forms/FieldRenderer';
import {
  SectionNav,
  useActiveSectionId,
  type SectionNavItem,
} from '../components/forms/SectionNav';
import { flattenFields } from '../utils/formSections';
import { evaluateRequiredWhen } from '../utils/requiredWhen';
import { ArrowLeftIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const FormFill: React.FC = () => {
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();
  const [searchParams] = useSearchParams();
  const { user, isParent, isOrgLevel } = useAuth();
  const { effectiveLocationId: userLocation } = useLocationContext();
  const scopedPath = useScopedPath();

  // The invitation is the only source of truth for which student this fill is
  // for. REGISTRATION forms always arrive with one (auto-trigger creates it).
  // CONSENT distributions also arrive with one.
  const invitationId = searchParams.get('invitationId');

  // Form state — keyed by `field.key` to match the backend's response shape.
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data: formData,
    isLoading: isLoadingForm,
    error: formError,
  } = useGetFormQuery(formId || '', {
    skip: !formId,
  });

  const [submitResponse, { isLoading: isSubmitting }] = useSubmitFormResponseMutation();
  const [getUploadUrl] = useGetUploadUrlMutation();

  const { data: parentStudents } = useLocalGetStudentsByParentQuery(user?.id || '', {
    skip: !isParent || !user?.id,
  });

  const { data: allStudents } = useLocalGetStudentsQuery(userLocation || '', {
    skip: isParent || (!userLocation && !isOrgLevel),
  });

  const students = isParent ? parentStudents : allStudents;

  const form = formData?.form;

  const { data: myInvites } = useGetMyInvitationsQuery(undefined, {
    skip: !invitationId,
  });
  const invitation = myInvites?.invitations?.find((i) => i.id === invitationId);
  const invitationStudentId = invitation?.studentId;

  const allFields = useMemo(() => (form ? flattenFields(form) : []), [form]);
  const studentSelectorField = allFields.find((f) => f.type === FormFieldType.STUDENT_SELECTOR);
  const selectedStudentId =
    invitationStudentId ||
    (studentSelectorField
      ? (responses[responseKey(studentSelectorField)] as string | undefined)
      : undefined) ||
    '';

  // For REGISTRATION forms, pre-fill the parent-facing fields from whatever
  // the staff captured on the Student row (typically just first/last name).
  // The parent can edit any pre-filled value before submitting. We only seed
  // empty fields so we don't trample the parent's edits on re-renders. Keys
  // match the seeded Default Registration Form template.
  useEffect(() => {
    if (!form || form.formType !== FormType.REGISTRATION) return;
    if (!selectedStudentId) return;
    const selected = students?.find((s) => s.id === selectedStudentId);
    if (!selected) return;

    const seeds: Record<string, string> = {};
    if (selected.firstName) seeds['child_first_name'] = selected.firstName;
    if (selected.lastName) seeds['child_last_name'] = selected.lastName;
    if (selected.dateOfBirth) seeds['child_dob'] = selected.dateOfBirth;
    const sp = selected.secondParent;
    if (sp?.name) seeds['second_parent_name'] = sp.name;
    if (sp?.email) seeds['second_parent_email'] = sp.email;
    if (sp?.relationship) seeds['second_parent_relationship'] = sp.relationship;

    setResponses((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [k, v] of Object.entries(seeds)) {
        if (next[k] === undefined || next[k] === '') {
          next[k] = v;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [form, selectedStudentId, students]);

  // When a `requiredWhen` parent flips off (e.g. has_allergies → no), drop
  // any dependent response so we don't POST stale answers the parent can no
  // longer see. Done as an effect so it covers parent flips initiated by
  // anything that mutates `responses`, not just direct user edits.
  useEffect(() => {
    if (!form) return;
    const toClear: string[] = [];
    for (const field of allFields) {
      if (!field.requiredWhen) continue;
      if (
        !evaluateRequiredWhen(field.requiredWhen, responses) &&
        responses[responseKey(field)] !== undefined
      ) {
        toClear.push(responseKey(field));
      }
    }
    if (toClear.length > 0) {
      setResponses((prev) => {
        const next = { ...prev };
        for (const k of toClear) delete next[k];
        return next;
      });
    }
  }, [form, allFields, responses]);

  const handleFieldChange = (fieldKey: string, value: any) => {
    setResponses((prev) => ({ ...prev, [fieldKey]: value }));
    if (errors[fieldKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedStudentId) {
      if (studentSelectorField) {
        newErrors[responseKey(studentSelectorField)] = 'Please select a student';
      } else {
        newErrors['_studentId'] =
          'No student is associated with this form. Open it from your registration link.';
      }
    }

    allFields.forEach((field) => {
      if (form && !isFieldTypeAllowedForFormType(field.type, form.formType)) return;
      // Skip validation on fields the parent can't see right now — a hidden
      // `requiredWhen` field can't be blocking. The backend re-validates with
      // the same rule so this is a UX shortcut, not an authorization gap.
      if (!evaluateRequiredWhen(field.requiredWhen, responses)) return;
      const rk = responseKey(field);
      if (field.required) {
        const value = responses[rk];
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        ) {
          newErrors[rk] = 'This field is required';
        }
      }

      const value = responses[rk];
      if (value !== undefined && value !== null && value !== '') {
        if (field.type === FormFieldType.NUMBER && field.validation) {
          const numValue = Number(value);
          if (field.validation.min !== undefined && numValue < field.validation.min) {
            newErrors[rk] = `Minimum value is ${field.validation.min}`;
          }
          if (field.validation.max !== undefined && numValue > field.validation.max) {
            newErrors[rk] = `Maximum value is ${field.validation.max}`;
          }
        }

        if (field.type === FormFieldType.FILE && field.validation?.maxFileSize) {
          if (value.fileSize > field.validation.maxFileSize) {
            newErrors[rk] = `File size exceeds maximum allowed (${Math.round(
              field.validation.maxFileSize / (1024 * 1024)
            )} MB)`;
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      const fileFields = allFields.filter((f) => f.type === FormFieldType.FILE);
      const uploadedFiles: Array<{
        fieldId: string;
        fileId: string;
        fileName: string;
        fileType: string;
        fileSize: number;
        s3Key: string;
      }> = [];

      for (const field of fileFields) {
        const fileData = responses[responseKey(field)];
        if (fileData?.file) {
          const uploadResult = await getUploadUrl({
            purpose: 'FORM_ATTACHMENT',
            contextId: formId!,
            mimeType: fileData.fileType,
            sizeBytes: fileData.fileSize,
            fileName: fileData.fileName,
          }).unwrap();

          const putResp = await fetch(uploadResult.uploadUrl, {
            method: 'PUT',
            body: fileData.file,
            headers: { 'Content-Type': fileData.fileType },
          });
          if (!putResp.ok) {
            throw new Error(
              `Failed to upload ${fileData.fileName}: ${putResp.status} ${putResp.statusText}`
            );
          }

          // The new /uploads/presign endpoint doesn't return a separate
          // fileId — the uuid is embedded in the s3Key as the leading
          // segment of the final path component. Extract it so we keep
          // sending a `fileId` field downstream (the submit-response zod
          // schema still expects it for back-compat; it's never read).
          const lastSegment = uploadResult.s3Key.split('/').pop() || '';
          const fileId = lastSegment.split('-')[0];

          uploadedFiles.push({
            fieldId: field.id,
            fileId,
            fileName: fileData.fileName,
            fileType: fileData.fileType,
            fileSize: fileData.fileSize,
            s3Key: uploadResult.s3Key,
          });
        }
      }

      const cleanResponses: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(responses)) {
        const field = allFields.find((f) => responseKey(f) === key);
        if (field?.type !== FormFieldType.FILE) {
          cleanResponses[key] = value;
        }
      }

      const submitted = await submitResponse({
        formId: formId!,
        studentId: selectedStudentId,
        responses: cleanResponses,
        files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
        invitationId: invitationId || undefined,
      }).unwrap();

      // Parent-facing REGISTRATION submits get a dedicated thank-you page —
      // it's the only one-shot, milestone flow. Staff-driven submits keep the
      // inline success card so they can log another response in-place.
      const isParentRegistration =
        isParent && invitationStudentId && form?.formType === FormType.REGISTRATION;
      if (isParentRegistration) {
        const responseId =
          (submitted as { response?: { id?: string } } | undefined)?.response?.id ?? '';
        const search = new URLSearchParams();
        if (responseId) search.set('responseId', responseId);
        if (invitationStudentId) search.set('studentId', invitationStudentId);
        navigate(`/forms/${formId}/thanks?${search.toString()}`);
        return;
      }
      setSubmitSuccess(true);
    } catch (error: any) {
      setSubmitError(
        error.data?.error || error.data?.message || 'Failed to submit form. Please try again.'
      );
    }
  };

  const sections = form?.sections ?? [];
  const sectionIds = sections.map((s) => s.id);
  const activeSectionId = useActiveSectionId(sectionIds);

  // Every CONTACT_LIST field on this form + its current entries. Threaded
  // into FieldRenderer so each contact list can offer "add from [other list]"
  // — the parent shouldn't have to retype an emergency contact who's also an
  // authorised-pickup contact (or vice-versa).
  const contactListFields = useMemo(() => {
    return sections
      .flatMap((s) => s.fields)
      .filter((f) => f.type === FormFieldType.CONTACT_LIST)
      .map((f) => ({
        fieldId: f.id,
        label: f.label,
        contacts: Array.isArray(responses[responseKey(f)])
          ? (responses[responseKey(f)] as Array<Record<string, unknown>>).map((r, idx) => ({
              id: String(r.id ?? `row-${idx}`),
              name: String(r.name ?? ''),
              relationship: String(r.relationship ?? ''),
              relationshipType: String(r.relationshipType ?? 'OTHER'),
              phone: String(r.phone ?? ''),
            }))
          : [],
      }));
  }, [sections, responses]);

  const navItems: SectionNavItem[] = sections.map((s) => {
    const visible = s.fields.filter((f) => evaluateRequiredWhen(f.requiredWhen, responses));
    const total = visible.length;
    const completed = visible.filter((f) => {
      const v = responses[responseKey(f)];
      return v !== undefined && v !== null && v !== '';
    }).length;
    return { id: s.id, title: s.title, completed, total };
  });

  const handleJump = (sectionId: string) => {
    const el = document.getElementById(`form-section-${sectionId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoadingForm) {
    return (
      <div className="py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (formError || !form) {
    return (
      <div className="py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Form not found</h3>
            <p className="mt-1 text-sm text-gray-500">
              The form you're looking for doesn't exist or is no longer available.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate(scopedPath('/forms'))}
                data-testid="form-not-found-back"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5" />
                Back to Forms
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    const isOneShotSubmission = !!invitationId;
    const isParentRegistration =
      isParent && invitationStudentId && form?.formType === FormType.REGISTRATION;
    const backHref = isParentRegistration
      ? `/parent/${invitationStudentId}`
      : isParent && invitationStudentId
      ? `/parent/${invitationStudentId}/forms`
      : scopedPath('/forms');
    const backLabel = isParentRegistration
      ? `Continue to ${invitation?.studentName?.split(' ')[0] || 'home'}`
      : 'Back to Forms';
    return (
      <div className="py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12 bg-white shadow rounded-lg">
            <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {isParentRegistration ? 'Registration complete!' : 'Form Submitted Successfully!'}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {isParentRegistration
                ? `${
                    invitation?.studentName?.split(' ')[0] || 'Your child'
                  } is all set. The location now has everything they need.`
                : `Your response to "${form.title}" has been recorded.`}
            </p>
            <div className="mt-6 space-x-3">
              {!isOneShotSubmission && (
                <button
                  onClick={() => {
                    setResponses({});
                    setSubmitSuccess(false);
                  }}
                  data-testid="form-submit-another"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Submit Another Response
                </button>
              )}
              <button
                onClick={() => navigate(backHref)}
                data-testid="form-submit-back"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                {backLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="mx-auto flex max-w-6xl gap-8 px-4 sm:px-6 lg:px-8">
        <SectionNav sections={navItems} activeId={activeSectionId} onJump={handleJump} />
        <div className="min-w-0 flex-1">
          <div className="mb-6">
            <button
              onClick={() => navigate(scopedPath('/forms'))}
              data-testid="form-back"
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              Back to Forms
            </button>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-5 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-900">{form.title}</h1>
              {form.description && <p className="mt-2 text-sm text-gray-600">{form.description}</p>}
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-8">
              {sections.map((section) => {
                const visibleFields = section.fields
                  .filter((f) => !(invitation && f.type === FormFieldType.STUDENT_SELECTOR))
                  .filter((f) => isFieldTypeAllowedForFormType(f.type, form.formType))
                  .filter((f) => evaluateRequiredWhen(f.requiredWhen, responses))
                  .sort((a, b) => a.order - b.order);
                if (visibleFields.length === 0) return null;
                return (
                  <section
                    key={section.id}
                    id={`form-section-${section.id}`}
                    data-testid={`form-section-${section.id}`}
                    className="space-y-4 scroll-mt-4"
                  >
                    {section.title && (
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
                        {section.description && (
                          <p className="mt-1 text-sm text-gray-600">{section.description}</p>
                        )}
                      </div>
                    )}
                    <div className="space-y-4">
                      {visibleFields.map((field) => (
                        <FieldRenderer
                          key={field.id}
                          field={field}
                          value={responses[responseKey(field)]}
                          onChange={(value) => handleFieldChange(responseKey(field), value)}
                          error={errors[responseKey(field)]}
                          students={students || []}
                          otherContactLists={contactListFields}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}

              {errors['_studentId'] && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{errors['_studentId']}</p>
                    </div>
                  </div>
                </div>
              )}

              {submitError && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{submitError}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate(scopedPath('/forms'))}
                    data-testid="form-cancel"
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="form-submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      'Submit Form'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormFill;
